import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notify } from "../../components/ui";
import { useOrderDraft } from "../../context/OrderDraftContext";
import { useSession } from "../../context/SessionContext";
import { useApi } from "../../hooks/useApi";
import { t } from "../../i18n";
import { showError } from "../../lib/feedback";
import { chatService, ConsultationFields } from "../../services/chat";
import { saveService } from "../../services/save";
import { Surgery } from "../../types/patient.types";
import { SaveResponse } from "../../types/save.types";
import { RAGResponse } from "../../types/rag.types";
import { PATHS } from "../../app/paths";

export const MAX_LAUDOS = 3;

type ProcedureRow = { code: string; description: string; quantity: string };

// "30715016 Artrodese via posterior x2, 40813136 Radioscopia x1" → linhas; o que não casa é descartado.
export const parseValidatedProcedures = (text?: string): ProcedureRow[] =>
  (text ? text.split(", ") : []).flatMap((item) => {
    const match = item.match(/^(\d+)\s+(.*?)\s+[xX](\d+)$/);
    return match ? [{ code: match[1], description: match[2], quantity: match[3] }] : [];
  });

// Sugestões da IA primeiro; dos procedimentos já salvos entram só os de código novo.
export const mergeProcedures = (suggested: string[][], saved: string[][]): string[][] => {
  const codes = new Set(suggested.map((item) => item[0]));
  return [...suggested, ...saved.filter((item) => !codes.has(item[0]))];
};

export const missingFields = (patientName: string, history: string, firstLaudo = ""): string[] =>
  [
    !patientName.trim() && t("newOrder.missingName"),
    !history.trim() && t("newOrder.missingHistory"),
    !firstLaudo.trim() && t("newOrder.missingLaudo"),
  ].filter((field): field is string => !!field);

// Cada palavra com a primeira letra maiúscula, como o nome sempre foi gravado.
export const capitalizeName = (value: string) => value.replace(/\b\w/g, (c) => c.toUpperCase());

const toRows = (items: ProcedureRow[]) => items.map((p) => [p.code, p.description, p.quantity]);

export const useNewOrder = () => {
  const draft = useOrderDraft();
  const { metadata } = useSession();
  const navigate = useNavigate();
  const createApi = useApi<{ timestamp: string }>();
  const saveApi = useApi<SaveResponse>();
  const { apiFetch } = createApi;
  const [analyzing, setAnalyzing] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);

  const { patientName, newInputRequest, laudos, laudoText, surgerie, savedCheckboxItems, modificadoHistorico, modificadoLaudo } = draft;

  // Pedido salvo aberto em "Editar pedido": preenche o rascunho com ele.
  useEffect(() => {
    if (surgerie && !modificadoHistorico && !modificadoLaudo) restore(surgerie);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surgerie]);

  const restore = (s: Surgery) => {
    const procedures = parseValidatedProcedures(s.procedimentos_validados);
    const rows = toRows(procedures);
    draft.setPatientName(s.pacient_name);
    draft.setnewInputRequest(s.historiaPaciente);
    draft.setTimestamp(s.timestamp);
    draft.setLaudos([s.laudoExames ?? ""]);
    draft.setSavedCheckboxItems(rows);
    draft.setInitialCheckboxItems(rows);
    draft.setCheckboxItems(rows);
    draft.setResumoOPME(s.resumoOPME ?? "");
    draft.setBirthDate(s.dataNasc ?? "");
    draft.setCpf(s.cpf ?? "");
    draft.setSexo(s.sexo ?? "");
    draft.setDocumentNumber(s.numCarteirinha ?? "");
    draft.setConvenio(s.convenio ?? "");
    draft.setDocumentValidity(s.valCarteirinha ?? "");
    draft.setHospital(s.hospital ?? "");
    draft.setDesiredDate(s.dataDesejada ?? "");
    draft.setSurgery(s.tipoCirurgia ?? "");
    draft.setEditableResponse(s.pedido_cirurgia ?? "");
    draft.setObservacoes(s.observacoesOpme ?? "");
    if (s.proceduresProps) {
      if (s.proceduresProps.tipo !== "escolha-livre") draft.setCheckedKit(procedures);
      draft.setSelectedButtonCheckKits(s.proceduresProps.tipo);
      draft.setSelectedProcedureKitIndex(s.proceduresProps.indice);
      draft.setSelectedCheckKitsType(s.proceduresProps.tipoDoPacote || "Pacotes Personalizados");
    }
    if (s.opmeProps) {
      if (s.opmeProps.tipo === "escolha-livre") {
        draft.setSelectedItems((s.opme_list ?? []).map(([item, quantity]) => ({ item, quantity: Number(quantity) })));
      }
      draft.setSelectedButtonOpmeKits(s.opmeProps.tipo);
      draft.setSelectedOpmeKitIndex(s.opmeProps.indice);
      draft.setSelectedOpmeKitsType(s.opmeProps.tipoDoPacote || "Pacotes Personalizados");
    }
  };

  const setHistory = (value: string) => {
    draft.setModificadoHistorico(true);
    draft.setnewInputRequest(value);
  };

  const setLaudo = (index: number, value: string) => {
    draft.setModificadoLaudo(true);
    draft.setLaudos((prev) => prev.map((laudo, i) => (i === index ? value : laudo)));
  };

  const addLaudo = () => draft.setLaudos((prev) => (prev.length < MAX_LAUDOS ? [...prev, ""] : prev));

  const removeLaudo = (index: number) => {
    draft.setModificadoLaudo(true);
    draft.setLaudos((prev) => prev.filter((_, i) => i !== index));
  };

  const applyConsultation = (fields: ConsultationFields) => {
    if (fields.nome_paciente) draft.setPatientName(fields.nome_paciente);
    if (fields.historico_paciente) setHistory(fields.historico_paciente);
    if (fields.laudo_paciente) setLaudo(0, fields.laudo_paciente);
    if (fields.hospital) draft.setHospital(fields.hospital);
    if (fields.tipo_de_cirurgia) draft.setSurgery(fields.tipo_de_cirurgia);
  };

  // Procedimentos sugeridos pela IA, já somados aos salvos. null = cancelado, erro ou nada encontrado.
  const suggestProcedures = async (signal: AbortSignal): Promise<string[][] | null> => {
    const query = `Histórico do cliente:\nO paciente ${patientName}, ${newInputRequest}\n————————————————————————————————————\nlaudo:\n${laudoText}`;
    let res: RAGResponse;
    try {
      res = await chatService.proceduresFromRag(apiFetch, { query, specialty: metadata.especialidade }, signal);
    } catch (err) {
      if (!signal.aborted) showError(err);
      return null;
    }
    // A IA só substitui o que encontrar: sem data no texto, o pedido salvo mantém a sua.
    if (res.hospital) draft.setHospital(res.hospital);
    if (res.surgery_data) draft.setDesiredDate(res.surgery_data);
    if (res.surgery_type) draft.setSurgery(res.surgery_type);
    const suggested = toRows(res.procedures || []);
    const merged = mergeProcedures(suggested, savedCheckboxItems);
    draft.setCheckboxItems(merged);
    draft.setInitialCheckboxItems(merged);
    draft.setSavedCheckboxItems(merged);
    if (!suggested.length) {
      notify({ tone: "warning", title: t("newOrder.noProcedures"), body: t("newOrder.noProceduresBody") });
      return null;
    }
    return merged;
  };

  // Pedido novo: cria e analisa. Pedido salvo: só reanalisa se o caso ou os laudos mudaram.
  const analyze = async () => {
    if (surgerie && savedCheckboxItems.length && !modificadoHistorico && !modificadoLaudo) {
      navigate(PATHS.procedures);
      return;
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    setAnalyzing(true);
    try {
      if (!surgerie) {
        const created = await createApi.request(() =>
          chatService.create(apiFetch, { user_email: metadata.email, pacient_name: patientName, laudoExames: laudoText, historiaPaciente: newInputRequest, especialidade: metadata.especialidade })
        );
        if (!created) return;
        draft.setTimestamp(created.timestamp);
      } else {
        const saved = await saveApi.request(() =>
          saveService.post(apiFetch, { timestamp: draft.timestamp, patient_name: patientName, laudoExames: laudoText, historiaPaciente: newInputRequest })
        );
        if (saved?.status !== "success") return;
      }
      const procedures = await suggestProcedures(controller.signal);
      if (!procedures) return;
      if (surgerie) {
        draft.setModificadoProcedimentos(true);
        draft.setEditableResponse("");
      }
      navigate(PATHS.procedures);
    } finally {
      controllerRef.current = null;
      setAnalyzing(false);
    }
  };

  const cancel = () => {
    controllerRef.current?.abort();
    setAnalyzing(false);
    notify({ tone: "info", title: t("newOrder.cancelledTitle"), body: t("newOrder.cancelledBody") });
  };

  return {
    patientName,
    setPatientName: (value: string) => draft.setPatientName(capitalizeName(value)),
    history: newInputRequest,
    setHistory,
    laudos,
    setLaudo,
    addLaudo,
    removeLaudo,
    applyConsultation,
    missing: missingFields(patientName, newInputRequest, laudos[0]),
    analyzing,
    analyze,
    cancel,
  };
};
